import React, { useContext, useMemo, useState } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { UserProfileContext } from '../contexts/UserProfileContext';
import { startTask } from '../services/api/tasks';
import TaskStatusBadge from '../../shared/components/TaskStatusBadge';
import LabelPill from '../../shared/components/pill/LabelPill';
import ConfirmStartModal from './modals/ConfirmStartModal';
import WorkflowDetails from './WorkflowDetails';
import formatDurationShort, { MS_PER_DAY } from '../../shared/utils/formatDuration';

function formatCompletedAt(isoOrString) {
  if (!isoOrString) return null;
  const d = typeof isoOrString === 'string' ? parseISO(isoOrString) : new Date(isoOrString);
  if (!isValid(d)) return null;
  return format(d, 'MMM d, yyyy · HH:mm');
}

// Below this much access left the line turns red. Two days is the point where a
// field engineer can still do something about it — raise it with the office,
// or get to the site — rather than discovering a dead link on arrival.
const ACCESS_ENDING_SOON_MS = 2 * MS_PER_DAY;

export default function AssignedTaskCard({ task, onStartTask, accessKey }) {
  const { userProfile, clearUserProfile } = useContext(UserProfileContext);
  const [isStarting, setIsStarting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const isCompleted = Boolean(task?.completed_at);

  // The span itself, kept separate from how it is written. It used to be one
  // string, which made "no expiry recorded" and "expired" indistinguishable to
  // everything downstream — including the Start button, which is disabled off
  // the back of it.
  const accessMsLeft = useMemo(() => {
    if (isCompleted) return null;
    const expiresAt = task?.access_key?.expires_at || task?.access_duration_until;
    if (!expiresAt) return null;
    const diff = new Date(expiresAt) - new Date();
    return Number.isNaN(diff) ? null : diff;
  }, [isCompleted, task?.access_key?.expires_at, task?.access_duration_until]);

  const accessLeftLabel = accessMsLeft > 0 ? formatDurationShort(accessMsLeft) : null;
  const accessEndingSoon = accessLeftLabel !== null && accessMsLeft < ACCESS_ENDING_SOON_MS;

  const completedAtLabel = useMemo(
    () => (isCompleted ? formatCompletedAt(task.completed_at) : null),
    [isCompleted, task?.completed_at]
  );

  const companyLine = useMemo(() => {
    const c = task?.completed_by_company_name?.trim();
    return c || null;
  }, [task?.completed_by_company_name]);

  const status = useMemo(() => {
    if (task?.completed_at) return 'completed';
    if (task?.started_at) return 'in_progress';
    if (!task?.assigned_to_email) return 'unassigned';
    return 'assigned';
  }, [task]);

  const buttonText = useMemo(() => {
    if (status === 'completed') return null;
    if (!task?.started_at) return 'Start Task';
    if (task?.started_by_field_engineer_id === userProfile?.field_engineer_id) return 'Continue Task';
    return 'Reassign to Me';
  }, [status, task, userProfile]);

  const handleStartClick = () => {
    if (!userProfile?.field_engineer_id) {
      alert('Your profile is incomplete. Please log in again.');
      clearUserProfile();
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmStart = async () => {
    setIsStarting(true);
    setShowConfirmation(false);
    try {
      await startTask(accessKey, task.id, {
        field_engineer_id: userProfile.field_engineer_id,
      });
      onStartTask?.(task);
    } catch (err) {
      console.error('Error starting task:', err);
      alert('Failed to start task. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  const isAccessExpired = !isCompleted && !accessLeftLabel;

  const customInstructions = useMemo(() => {
    const custom = task?.instructions?.trim();
    return custom || null;
  }, [task]);

  const assignedToYou =
    !isCompleted &&
    task?.assigned_to_email &&
    userProfile?.email &&
    task.assigned_to_email.toLowerCase() === userProfile.email.toLowerCase();

  return (
    <div className="card mb-3 shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {task?.ticket_reference && (
              <LabelPill size="touch" title="Ticket reference">
                #{task.ticket_reference}
              </LabelPill>
            )}
            <TaskStatusBadge status={status} size="touch" />
          </div>
        </div>

        <WorkflowDetails workflow={task?.workflow} />

        {assignedToYou && (
          <p className="text-muted small mt-2 mb-0">Assigned to you</p>
        )}

        {isCompleted && (
          <div className="mt-3 pt-3 border-top">
            {completedAtLabel ? (
              <div className="d-flex align-items-baseline gap-2 text-body-secondary small mb-1">
                <i className="bi bi-calendar-check flex-shrink-0" aria-hidden />
                <span>
                  <span className="text-muted">Completed</span>{' '}
                  <time className="text-body" dateTime={task.completed_at}>
                    {completedAtLabel}
                  </time>
                </span>
              </div>
            ) : (
              <div className="d-flex align-items-baseline gap-2 text-body-secondary small mb-1">
                <i className="bi bi-calendar-check flex-shrink-0" aria-hidden />
                <span className="text-muted">Completed</span>
              </div>
            )}
            {companyLine && (
              <div className="d-flex align-items-baseline gap-2 text-body-secondary small">
                <i className="bi bi-building flex-shrink-0" aria-hidden />
                <span>
                  <span className="text-muted">Organization</span>{' '}
                  <span className="text-body">{companyLine}</span>
                </span>
              </div>
            )}
          </div>
        )}

        {customInstructions && !isCompleted && (
          <div className="alert alert-info mb-0 mt-3 py-2 small">{customInstructions}</div>
        )}

        {buttonText && (
          <button
            type="button"
            className="btn btn-primary w-100 mt-3"
            onClick={handleStartClick}
            disabled={isStarting || isAccessExpired}
            aria-busy={isStarting ? 'true' : 'false'}
          >
            {isStarting ? 'Starting...' : buttonText}
          </button>
        )}

        {accessLeftLabel && (
          <p
            className={`small mt-3 mb-0 ${accessEndingSoon ? 'text-danger fw-medium' : 'text-muted'}`}
          >
            Remaining access: {accessLeftLabel}
          </p>
        )}
      </div>

      <ConfirmStartModal
        show={showConfirmation}
        title="Confirm Task Start"
        message="Are you at the site, ready to start working on this task?"
        confirmLabel="Yes, Start Task"
        cancelLabel="Cancel"
        onConfirm={handleConfirmStart}
        onCancel={() => setShowConfirmation(false)}
        isLoading={isStarting}
      />
    </div>
  );
}
